<template>
  <div v-if="visible" class="modal-overlay" @click="emitClose">
    <div class="topic-kline-modal" :class="theme" @click.stop>
      <div class="topic-kline-header">
        <h4 class="modal-title font-weight-bold">
          <b-icon icon="graph-up" class="mr-2 text-primary"></b-icon>
          {{ topicName }} - 题材K线图
        </h4>
        <button class="close-btn" @click="emitClose">&times;</button>
      </div>
      
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
        <div class="text-muted mt-2">正在加载K线数据...</div>
      </div>
      
      <div v-else-if="klineData.length === 0" class="text-center py-5 text-muted">
        暂无K线数据
      </div>
      
      <div v-else class="chart-container">
        <v-chart
          :options="chartOption"
          :theme="theme === 'dark' ? 'site-dark' : ''"
          autoresize
          style="width: 100%; height: 450px;"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ECharts from 'vue-echarts'
import { request_zzquant } from 'network/request.js'

export default {
  name: 'TopicKlineModal',
  components: {
    'v-chart': ECharts
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    uniqueKey: {
      type: String,
      required: true
    },
    topicName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      klineData: [],
      loading: false,
      chartOption: {},
      theme: 'light'
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.detectTheme()
        this.fetchKlineData()
      }
    },
    uniqueKey() {
      if (this.visible) {
        this.fetchKlineData()
      }
    }
  },
  created() {
    this.detectTheme()
    window.addEventListener('site-theme-changed', this.onThemeChanged)
  },
  beforeDestroy() {
    window.removeEventListener('site-theme-changed', this.onThemeChanged)
  },
  methods: {
    detectTheme() {
      try {
        const t = localStorage.getItem('site.theme')
        if (t) {
          this.theme = t
        }
      } catch (e) {
        console.warn('Failed to read theme:', e)
      }
    },
    onThemeChanged(e) {
      this.theme = e && e.detail && e.detail.theme ? e.detail.theme : (localStorage.getItem('site.theme') || 'light')
      if (this.klineData.length > 0) {
        this.generateChartOption()
      }
    },
    emitClose() {
      this.$emit('close')
    },
    fetchKlineData() {
      if (!this.uniqueKey) return
      this.loading = true
      this.klineData = []
      
      request_zzquant({
        url: `/v3/topic/table/${this.uniqueKey}/kline`,
        method: 'get'
      })
      .then(res => {
        const d = res.data || {}
        if (d.code === 20000 || d.code === 200) {
          this.klineData = d.data || []
          this.generateChartOption()
        } else {
          console.error('Failed to load topic kline data:', d.message)
        }
      })
      .catch(err => {
        console.error('Network error fetching topic kline:', err)
      })
      .finally(() => {
        this.loading = false
      })
    },
    generateChartOption() {
      if (!this.klineData || this.klineData.length === 0) return
      
      const dates = this.klineData.map(d => d.trade_date)
      const values = this.klineData.map(d => [d.open, d.close, d.low, d.high, d.prev_close])
      const volumes = this.klineData.map(d => d.amount)
      
      const isDark = this.theme === 'dark'
      const upColor = isDark ? '#ef4444' : '#ec0000'
      const downColor = isDark ? '#10b981' : '#00da3c'
      const textColor = isDark ? '#94a3b8' : '#64748b'
      const gridBorderColor = isDark ? '#334155' : '#e2e8f0'
      
      this.chartOption = {
        animation: false,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          backgroundColor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderColor: isDark ? '#334155' : '#cbd5e1',
          textStyle: { color: isDark ? '#f1f5f9' : '#1e293b', fontSize: 12 },
          formatter: (params) => {
            let res = params[0].name + '<br/>'
            let klineHtml = ''
            let volumeHtml = ''
            
            params.forEach(p => {
              if (p.seriesName === '题材K') {
                const val = p.value // [index, open, close, low, high, prev_close]
                const open = val[1]
                const close = val[2]
                const low = val[3]
                const high = val[4]
                const prev = val[5] || open
                const change = ((close - prev) / prev * 100).toFixed(2)
                const changeColor = close >= prev ? upColor : downColor
                
                klineHtml = `${p.marker} 指数: <span style="color:${changeColor};font-weight:bold">${close} (${change > 0 ? '+' : ''}${change}%)</span><br/>`
                klineHtml += `开盘: ${open} 最高: ${high} 最低: ${low}<br/>`
              } else if (p.seriesName === '成交额') {
                const vol = p.value
                volumeHtml = `${p.marker} 成交额: <span style="font-weight:bold">${(vol / 100000000).toFixed(2)} 亿</span><br/>`
              }
            })
            return res + klineHtml + volumeHtml
          }
        },
        grid: [
          { left: '8%', right: '8%', height: '60%', top: '10%' },
          { left: '8%', right: '8%', top: '75%', height: '15%' }
        ],
        xAxis: [
          {
            type: 'category',
            data: dates,
            scale: true,
            boundaryGap: false,
            axisLine: { lineStyle: { color: textColor } },
            splitLine: { show: false },
            axisLabel: { fontSize: 10 }
          },
          {
            gridIndex: 1,
            type: 'category',
            data: dates,
            scale: true,
            boundaryGap: false,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false }
          }
        ],
        yAxis: [
          {
            scale: true,
            axisLine: { lineStyle: { color: textColor } },
            splitLine: { show: true, lineStyle: { color: gridBorderColor, type: 'dashed' } },
            axisLabel: { fontSize: 10 }
          },
          {
            gridIndex: 1,
            scale: true,
            splitNumber: 2,
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false }
          }
        ],
        axisPointer: {
          link: { xAxisIndex: 'all' }
        },
        dataZoom: [
          {
            type: 'inside',
            xAxisIndex: [0, 1],
            start: dates.length > 60 ? 100 - (60 / dates.length * 100) : 0,
            end: 100
          },
          {
            show: true,
            xAxisIndex: [0, 1],
            type: 'slider',
            top: '92%',
            height: 12,
            start: dates.length > 60 ? 100 - (60 / dates.length * 100) : 0,
            end: 100,
            textStyle: { color: textColor }
          }
        ],
        series: [
          {
            name: '题材K',
            type: 'candlestick',
            data: values,
            itemStyle: {
              color: upColor,
              color0: downColor,
              borderColor: upColor,
              borderColor0: downColor
            }
          },
          {
            name: '成交额',
            type: 'bar',
            xAxisIndex: 1,
            yAxisIndex: 1,
            data: volumes,
            itemStyle: {
              color: (params) => {
                const i = params.dataIndex
                return values[i][1] >= values[i][0] ? upColor : downColor
              }
            }
          }
        ]
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  padding: 15px;
}

.topic-kline-modal {
  width: 100%;
  max-width: 800px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 20px;
  color: #1e293b;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.topic-kline-modal.dark {
  background: #1a1e29;
  color: #f1f5f9;
  border-color: #334155;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
}

.topic-kline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 10px;
}

.topic-kline-modal.dark .topic-kline-header {
  border-bottom-color: #334155;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.75rem;
  cursor: pointer;
  color: #64748b;
  line-height: 1;
  padding: 0;
}

.close-btn:hover {
  color: #ef4444;
}

.chart-container {
  width: 100%;
  position: relative;
}
</style>
